<!--
FlotChart Component A reusable chart component built on top of jQuery Flot
for creating line and bar charts. Example usage: ```vue

Basic usage with line chart

<flot-chart
    chart-id="myChart"
    :data="[
        [0, 10],
        [1, 20],
        [2, 15]
    ]"
    :xaxis-labels="[
        [0, 'Jan'],
        [1, 'Feb'],
        [2, 'Mar']
    ]"
    :height="300"
/>

Usage with ranges and custom options

<flot-chart
    chart-id="myChart"
    :data="[
        [0, 10],
        [1, 20],
        [2, 15]
    ]"
    :xaxis-labels="[
        [0, 'Jan'],
        [1, 'Feb'],
        [2, 'Mar']
    ]"
    :ranges="[{ min: 0, max: 25, color: '#CAF6BF' }]"
    :options="{ series: { lines: { fill: true } } }"
/>
-->
<template>
    <div class="chart-wrapper">
        <div :id="chartId" class="chart-container"></div>
    </div>
</template>

<script>
import "@/jquery/jquery.flot";
import "@/jquery/jquery.flot.tooltip";
import "@/jquery/jquery.flot.stack";
import "@/jquery/jquery.flot.axislabels";

export default {
    name: "FlotChart",
    props: {
        chartId: {
            type: String,
            required: true
        },
        data: {
            type: Array,
            required: true
        },
        height: {
            type: Number,
            default: 240
        },
        type: {
            type: String,
            default: "line", // 'line', 'bar'
            validator: function (value) {
                return ["line", "bar"].indexOf(value) !== -1;
            }
        },
        color: {
            type: String,
            default: "#000"
        },
        xaxisLabels: {
            type: Array,
            default: () => []
        },
        tooltipFormat: {
            type: String,
            default: "%y"
        },
        options: {
            type: Object,
            default: () => ({})
        },
        ranges: {
            type: Array,
            default: () => []
        },
        yaxisUnit: {
            type: String,
            default: ""
        }
    },
    data() {
        return {
            chart: null,
            defaultOptions: {
                series: {
                    lines: {
                        show: true,
                        lineWidth: 1,
                        fill: false
                    },
                    points: {
                        show: true,
                        radius: 3
                    },
                    bars: {
                        show: false,
                        barWidth: 0.6,
                        align: "center"
                    }
                },
                grid: {
                    borderWidth: 0,
                    hoverable: true,
                    margin: {
                        bottom: 60,
                        left: 20,
                        right: 20
                    },
                    markings: function (axes) {
                        return []; // Will be updated in computed
                    }
                },
                xaxis: {
                    ticks: [],
                    rotateTicks: 20 // Add rotation angle
                },
                yaxis: {
                    tickDecimals: 0,
                    tickFormatter: function (val) {
                        return val
                            .toString()
                            .replace(/\B(?=(?:\d{3})+(?!\d))/g, ",");
                    },
                    axisLabel: this.yaxisUnit,
                    axisLabelUseCanvas: true,
                    axisLabelFontSizePixels: 12,
                    axisLabelFontFamily: "Verdana, Arial",
                    axisLabelPadding: 10,
                    position: "left" // Ensure label appears on the left
                },
                tooltip: true,
                tooltipOpts: {
                    content: "%y",
                    shifts: {
                        x: 20,
                        y: 0
                    },
                    defaultTheme: false
                }
            }
        };
    },
    computed: {
        decodedUnit() {
            const temp = document.createElement("div");
            temp.innerHTML = this.yaxisUnit;
            return temp.textContent || temp.innerText;
        },
        chartOptions() {
            let options = { ...this.defaultOptions };
            let optionProp = { ...this.options };

            // Find min and max values from data
            const dataValues = this.data.map(point => point[1]);
            const dataMin = Math.min(...dataValues);
            const dataMax = Math.max(...dataValues);

            // Set default y-axis range based on data
            if (optionProp.yaxis) {
                options.yaxis = { ...options.yaxis, ...optionProp.yaxis };
            }

            options.yaxis = {
                ...options.yaxis,
                min: dataMin * 0.9,
                max: dataMax * 1.1
            };

            if (this.ranges.length > 0) {
                // Get valid range values (non-null, non-undefined)
                const validMinRanges = this.ranges
                    .map(r => r.min)
                    .filter(val => val != null && val != undefined);
                const validMaxRanges = this.ranges
                    .map(r => r.max)
                    .filter(val => val != null && val != undefined);

                // Only calculate if we have valid values
                const rangeMin =
                    validMinRanges.length > 0
                        ? Math.min(...validMinRanges)
                        : null;
                const rangeMax =
                    validMaxRanges.length > 0
                        ? Math.max(...validMaxRanges)
                        : null;

                // Set y-axis limits only if valid range values exist
                if (rangeMin !== null && dataMin > rangeMin) {
                    options.yaxis.min = rangeMin * 0.5;
                }

                if (rangeMax !== null && dataMax < rangeMax) {
                    options.yaxis.max = Math.max(dataMax * 1.5, rangeMax);
                }

                // Update markings with ranges, only for ranges that have valid values
                options.grid.markings = axes => {
                    return this.ranges
                        .filter(
                            range =>
                                range.min != null &&
                                range.min != undefined &&
                                range.max != null &&
                                range.max != undefined
                        )
                        .map(range => ({
                            yaxis: {
                                from: range.min ?? axes.yaxis.min,
                                to: range.max ?? axes.yaxis.max
                            },
                            color: range.color
                        }));
                };
            }

            // Set y-axis formatting
            options.yaxis = {
                ...options.yaxis,
                axisLabel: this.decodedUnit,
                tickDecimals: 2,
                tickFormatter: val => {
                    return val.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                }
            };

            // Set chart type
            if (this.type === "bar") {
                options.series.bars.show = true;
                options.series.lines.show = false;
                options.series.points.show = false;
            }

            // Set x-axis labels with limit
            if (this.xaxisLabels.length) {
                const totalLabels = this.xaxisLabels.length;
                let ticks;

                if (totalLabels <= 8) {
                    // Show all labels if 8 or fewer
                    ticks = this.xaxisLabels.map((label, index) => {
                        return [index, label[1] + "\u200B".repeat(index)];
                    });
                } else {
                    // Show 8 labels distributed evenly
                    const step = Math.ceil((totalLabels - 2) / 6); // Calculate step for 6 middle points
                    ticks = [];

                    // Add first label
                    ticks.push([
                        0,
                        this.xaxisLabels[0][1] + "\u200B".repeat(0)
                    ]);

                    // Add 6 evenly distributed middle labels
                    for (let i = step; i < totalLabels - 1; i += step) {
                        if (ticks.length < 7) {
                            // Ensure we don't add more than 7 (including first)
                            ticks.push([
                                i,
                                this.xaxisLabels[i][1] + "\u200B".repeat(i)
                            ]);
                        }
                    }

                    // Add last label
                    let lastLabel = this.xaxisLabels[totalLabels - 1][1];
                    ticks.push([
                        totalLabels - 1,
                        lastLabel + "\u200B".repeat(totalLabels - 1)
                    ]);

                    // Fill in empty ticks for unlabeled points
                    this.xaxisLabels.forEach((label, index) => {
                        if (!ticks.find(tick => tick[0] === index)) {
                            ticks.push([index, ""]);
                        }
                    });

                    // Sort by index
                    ticks.sort((a, b) => a[0] - b[0]);
                }

                options.xaxis.ticks = ticks;

                if (optionProp.xaxis) {
                    optionProp.xaxis.ticks = ticks;
                }
            }

            // Set tooltip format
            options.tooltipOpts = {
                ...options.tooltipOpts,
                content: (label, x, y, item) => {
                    return (
                        this.xaxisLabels[x][1] + "<br/>%y " + this.decodedUnit
                    );
                }
            };

            options.grid.margin = {
                top: 20,
                right: 80,
                bottom: 40,
                left: 20
            };

            // Merge with custom options
            return { ...options, ...optionProp };
        },
        chartData() {
            return [
                {
                    data: this.data,
                    color: this.color,
                    label: ""
                }
            ];
        }
    },
    watch: {
        data: {
            handler() {
                this.$nextTick(() => {
                    this.renderChart();
                });
            },
            deep: true
        },
        options: {
            handler() {
                this.$nextTick(() => {
                    this.renderChart();
                });
            },
            deep: true
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.renderChart();
            this.handleResize();
        });
    },
    methods: {
        renderChart() {
            if (!this.data.length) return;

            const container = $(`#${this.chartId}`);
            container.height(this.height);

            try {
                // Check if $.plot is available
                if (typeof $.plot !== "function") {
                    console.error("Flot is not properly loaded");
                    return;
                }

                this.chart = $.plot(
                    container,
                    this.chartData,
                    this.chartOptions
                );

                $(`#${this.chartId} .xAxis .tickLabel`).css({
                    transform: `rotate(${this.chartOptions.xaxis.rotateTicks}deg)`,
                    "transform-origin": "top left",
                    "margin-top": "0px",
                    "margin-left": "30px"
                });
            } catch (error) {
                console.error("Error rendering chart:", error);
            }
        },
        handleResize() {
            const self = this;
            $(window).resize(() => {
                const container = $(`#${this.chartId}`);
                container.height(this.height);
                self.renderChart();
            });
        }
    },
    beforeDestroy() {
        $(window).off("resize");
    }
};
</script>

<style scoped>
.chart-wrapper {
    position: relative;
    width: 100%;
}

.chart-container {
    width: 100% !important;
    min-height: v-bind(height + "px");
}

:deep(.xAxis .tickLabel) {
    white-space: nowrap;
}

/* Ensure the plot is full width */
:deep(.flot-base),
:deep(.flot-text) {
    width: 100% !important;
    left: 0 !important;
}

:deep(.flot-overlay) {
    width: 100% !important;
}
</style>
